"use client";

// components/BrandCard.tsx or ./BrandCard.tsx
import Image from "next/image";
import { useState } from "react";
import DOMPurify from "isomorphic-dompurify";

interface BrandCardProps {
  brand: {
    clientLogo: string;
    tagLine: string;
    smallOverview?: string;
  };
}

const BrandCard = ({ brand }: BrandCardProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="relative w-full h-[180px] md:h-[200px] lg:h-[220px] bg-white border border-[#ecf0f6] flex items-center justify-center px-[20px] md:px-[30px] lg:px-[40px] overflow-hidden transition-all duration-300 cursor-pointer group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Default State: Logo + Tagline */}
      <div
        className={`absolute inset-0 flex flex-col items-center justify-center gap-[16px] md:gap-[20px] lg:gap-[24px] transition-opacity duration-300 ${
          isHovered ? "opacity-0" : "opacity-100"
        }`}
      >
        <div className="flex items-center justify-center h-[40px] md:h-[50px] lg:h-[57px]">
          <Image
            src={brand.clientLogo || "/fallback-logo.png"}
            alt={brand.tagLine || "Client logo"}
            width={148}
            height={57}
            className="max-w-[100px] md:max-w-[130px] lg:max-w-[148px] max-h-[40px] md:max-h-[50px] lg:max-h-[57px] w-auto h-auto object-contain"
          />
        </div>
        <h3 className="text-[#00040b] text-[16px] md:text-[17px] lg:text-[18px] font-semibold clash-font text-center">
          {brand.tagLine}
        </h3>
      </div>

      {/* Hover State: Blue overlay with tagline + description */}
      <div
        className={`absolute inset-0 bg-[#225bd4] flex flex-col items-center justify-center px-[20px] md:px-[25px] lg:px-[30px] text-center transition-opacity duration-300 ${
          isHovered ? "opacity-100" : "opacity-0"
        }`}
      >
        <h3 className="text-white text-[16px] md:text-[17px] lg:text-[18px] font-semibold clash-font mb-[8px] md:mb-[10px]">
          {brand.tagLine}
        </h3>
        {brand.smallOverview && (
          <p
            className="text-white text-[14px] md:text-[15px] lg:text-[16px] font-medium italic leading-[140%]"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                brand.smallOverview
                  .replace(/\n/g, "<br>")
                  .replace(/<b>(.*?)<\/b>/gi, "<strong>$1</strong>")
                  .replace(/<i>(.*?)<\/i>/gi, "<em>$1</em>"),
              ),
            }}
          />
        )}
      </div>
    </div>
  );
};

export default BrandCard;
